import { ReactElement, useState } from "react";
import { IInputValidation } from "client/Validation/input";
import { IInputValueConverter, ConversionError } from "client/Forms/converters";
import { TextInputProps, TextInput } from "../TextInput/TextInput";
import { useEffectSkipFirstRender } from "client/Hooks";

type IInputProps<TInputValue> = {
	value: TInputValue;
	onBlur?(newValue: TInputValue): void;
	isInvalid?: boolean;
} & ({ onChange(value: TInputValue): void; isDisabled?: boolean;} | { isDisabled: true; onChange?(value: TInputValue): void; });

export type ConvertedTextInputProps<TValue> =
	Omit<TextInputProps, keyof IInputProps<string>> & {
	validation?: IInputValidation;
} & IInputProps<TValue>;


export const convertibleTextInput = <TValue extends unknown>(
	converter: IInputValueConverter<TValue, string>
): (props: ConvertedTextInputProps<TValue>) => ReactElement => {
	const ConvertibleTextInput = (props: ConvertedTextInputProps<TValue>): ReactElement => {
		const {
			value,
			onChange,
			onBlur,
			validation
		} = props;

		const [currentValue, setCurrentValue] = useState<TValue | ConversionError<string>>(value);

		useEffectSkipFirstRender(
			(): void => {
				setCurrentValue(value);
			},
			[value]
		);

		const onChangeInvocator = (newValue: string) => {
			const parsedValue: TValue | ConversionError<string> = converter.parseValue(newValue);

			setCurrentValue(parsedValue);

			if (!(parsedValue instanceof ConversionError) && onChange) {
				onChange(parsedValue);
			}

			if (validation) {
				validation.triggerValueChanged({
					conversionError: parsedValue instanceof ConversionError
						? parsedValue
						: undefined
				});
			}
		};

		const onBlurInvocator = (newValue: string) => {
			const parsedValue: TValue | ConversionError<string> = converter.parseValue(newValue);

			if (validation) {
				validation.triggerInputFinished({
					conversionError: parsedValue instanceof ConversionError
						? parsedValue
						: undefined
				});
			}

			if (!(parsedValue instanceof ConversionError) && onBlur) {
				onBlur(parsedValue);
			}
		};

		const inputValue: string = currentValue instanceof ConversionError
			? currentValue.inputValue
			: converter.formatValue(currentValue);

		return (
			<TextInput
				{...props}
				value={inputValue}
				onChange={onChangeInvocator}
				onBlur={onBlurInvocator}
			/>
		);
	};

	ConvertibleTextInput.DisplayName = "ConvertibleTextInput";

	return ConvertibleTextInput;
};
